import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import patientService from '../../services/patientService';
import './AbnormalReadings24hModal.css';

/** Keep the stored clock digits. Do not shift an ISO "Z" value into local time. */
const wallClockParts = (dateStr) => {
  const match = String(dateStr || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5])
  };
};

const wallClockMs = (dateStr) => {
  const parts = wallClockParts(dateStr);
  if (!parts) return NaN;
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute).getTime();
};

/** Format date/time from the measurement clock, not the upload clock. */
const formatReadingTime = (dateStr) => {
  const parts = wallClockParts(dateStr);
  if (!parts) return '';
  const now = new Date();
  const isToday =
    parts.year === now.getFullYear() &&
    parts.month === now.getMonth() + 1 &&
    parts.day === now.getDate();

  const period = parts.hour >= 12 ? 'PM' : 'AM';
  const displayHour = parts.hour % 12 || 12;
  const timePart = `${displayHour}:${String(parts.minute).padStart(2, '0')} ${period}`;
  if (isToday) return `Today ${timePart}`;
  const monthName = new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString([], {
    month: 'short',
    day: 'numeric'
  });
  return `${monthName} · ${timePart}`;
};

const getStatusColor = (status) => {
  if (status === 'high') return '#dc2626'; // Vibrant red
  if (status === 'low') return '#d97706';  // Amber
  return '#166534'; // Forest green
};

const getBpSysStatus = (sys, patient) => {
  if (sys == null || Number.isNaN(Number(sys)) || Number(sys) <= 0) return 'normal';
  const val = Number(sys);
  const max = Number(patient?.target_sys_max) || 140;
  const min = Number(patient?.target_sys_min) || 100;
  if (val > max) return 'high';
  if (val < min) return 'low';
  return 'normal';
};

const getBpDiaStatus = (dia, patient) => {
  if (dia == null || Number.isNaN(Number(dia)) || Number(dia) <= 0) return 'normal';
  const val = Number(dia);
  const max = Number(patient?.target_dia_max) || 90;
  const min = Number(patient?.target_dia_min) || 60;
  if (val > max) return 'high';
  if (val < min) return 'low';
  return 'normal';
};

const getBgStatus = (bg, patient) => {
  if (bg == null || Number.isNaN(Number(bg)) || Number(bg) <= 0) return 'normal';
  const val = Number(bg);
  const max = Number(patient?.target_bg_max) || 140;
  const min = Number(patient?.target_bg_min) || 70;
  if (val > max) return 'high';
  if (val < min) return 'low';
  return 'normal';
};

const getPulseStatus = (pulse, patient) => {
  if (pulse == null || Number.isNaN(Number(pulse)) || Number(pulse) <= 0) return 'normal';
  const val = Number(pulse);
  const max = Number(patient?.target_pulse_max) || 100;
  const min = Number(patient?.target_pulse_min) || 60;
  if (val > max) return 'high';
  if (val < min) return 'low';
  return 'normal';
};

const getWeightStatus = (wt, patient) => {
  if (wt == null || Number.isNaN(Number(wt)) || Number(wt) <= 0) return 'normal';
  const val = Number(wt);
  const max = Number(patient?.target_weight_max) || 220;
  const min = Number(patient?.target_weight_min) || 66;
  if (val > max) return 'high';
  if (val < min) return 'low';
  return 'normal';
};

const makeReadingKey = (r) => `${r.vital_type}_${r.id}`;

const AbnormalReadings24hModal = ({
  isOpen,
  onClose,
  patient,
  practiceId,
  currentUserId,
  initialSelectedKeys = [],
  onConfirm
}) => {
  const [readings, setReadings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  const patientId = patient?.id || patient?.patient_table_id || patient?.patient_id || patient?.user_id;
  const patientName =
    String(patient?.patient_name || [patient?.first_name, patient?.last_name].filter(Boolean).join(' ') || 'Patient').trim();

  // Load 24-hour abnormal readings whenever modal opens
  useEffect(() => {
    if (!isOpen || !practiceId || !patientId) return;

    let isMounted = true;
    setIsLoading(true);

    const fetchReadings = async () => {
      try {
        const res = await patientService.getPatientAbnormalReadings(practiceId, patientId, { hours: 24 * 7 });
        const raw = Array.isArray(res?.data?.data) ? res.data.data : [];
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const list = raw.filter((r) => {
          if (r.assigned_to_user_id) return false;
          const at = wallClockMs(r.reading_date);
          return !Number.isNaN(at) && at >= cutoff;
        });
        if (!isMounted) return;

        setReadings(list);

        const isAssigned = (r) => Boolean(r.assigned_to_user_id);
        const available = list.filter((r) => !isAssigned(r));
        const assignedKeys = list.filter(isAssigned).map(makeReadingKey);
        if (initialSelectedKeys && initialSelectedKeys.length > 0) {
          const validKeys = initialSelectedKeys.filter((k) => available.some((r) => makeReadingKey(r) === k));
          setSelectedKeys(new Set([...assignedKeys, ...validKeys]));
        } else {
          setSelectedKeys(new Set([...assignedKeys, ...available.map(makeReadingKey)]));
        }
      } catch (err) {
        console.warn('[AbnormalReadings24hModal] Error loading 24h readings:', err);
        if (isMounted) setReadings([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchReadings();
    return () => {
      isMounted = false;
    };
  }, [isOpen, practiceId, patientId, currentUserId, initialSelectedKeys]);

  // Compute available vs locked readings
  const { availableReadings, lockedReadings } = useMemo(() => {
    const avail = [];
    const locked = [];
    readings.forEach((r) => {
      if (r.assigned_to_user_id) {
        locked.push(r);
      } else {
        avail.push(r);
      }
    });
    return { availableReadings: avail, lockedReadings: locked };
  }, [readings]);

  const selectedAvailableCount = availableReadings.filter((r) => selectedKeys.has(makeReadingKey(r))).length;

  const allAvailableSelected =
    availableReadings.length > 0 &&
    selectedAvailableCount === availableReadings.length;

  const handleToggleSelectAll = () => {
    const lockedKeys = lockedReadings.map(makeReadingKey);
    if (allAvailableSelected) {
      setSelectedKeys(new Set(lockedKeys));
    } else {
      setSelectedKeys(new Set([...lockedKeys, ...availableReadings.map(makeReadingKey)]));
    }
  };

  const handleToggleRow = (reading) => {
    if (reading.assigned_to_user_id) return;

    const key = makeReadingKey(reading);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const chosenReadings = readings
      .filter((r) => !r.assigned_to_user_id && selectedKeys.has(makeReadingKey(r)))
      .map((r) => ({
        vitalType: r.vital_type,
        measurementId: r.id,
        vital_type: r.vital_type,
        id: r.id,
        key: makeReadingKey(r),
        readingValue: r.reading_value,
        vitalLabel: r.vital_label,
        readingDate: r.reading_date
      }));

    onConfirm(chosenReadings);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="ab24-overlay" onClick={onClose}>
      <div
        className="ab24-modal trend-7d-style-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="ab24-header">
          <div className="ab24-header-main">
            <h3 className="ab24-title">Abnormal Readings (Last 7 Days)</h3>
            <span className="ab24-subtitle">
              {patientName} {patient?.id ? `· ID #${patient.id}` : ''}
            </span>
          </div>
          <button
            type="button"
            className="ab24-close-btn"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Stats & Select All Bar (styled after trend-7d-hover-avg) */}
        <div className="ab24-summary-bar">
          <label className="ab24-select-all-label">
            <input
              type="checkbox"
              className="ab24-checkbox"
              checked={allAvailableSelected}
              onChange={handleToggleSelectAll}
              disabled={availableReadings.length === 0}
            />
            <span className="ab24-select-all-text">
              Select All Available ({availableReadings.length})
            </span>
          </label>
          <span className="ab24-counter-badge">
            {selectedAvailableCount} of {availableReadings.length} selected
            {lockedReadings.length > 0 ? ` · (${lockedReadings.length} assigned to others)` : ''}
          </span>
        </div>

        {/* Body / Readings List */}
        <div className="ab24-body">
          {isLoading ? (
            <div className="ab24-loading">Loading last 7 days readings…</div>
          ) : readings.length === 0 ? (
            <div className="ab24-empty">
              No unassigned abnormal readings in the last 7 days.
            </div>
          ) : (
            <div className="ab24-list">
              {readings.map((r, idx) => {
                const key = makeReadingKey(r);
                const isAssigned = Boolean(r.assigned_to_user_id);
                const isSelected = isAssigned || selectedKeys.has(key);
                const timeLabel = formatReadingTime(r.reading_date);

                // Vital shorthand
                const shortVital =
                  r.vital_type === 'bp' ? 'BP' : r.vital_type === 'bg' ? 'BG' : 'W';

                return (
                  <div
                    key={key}
                    className={`ab24-row ${idx % 2 === 0 ? 'is-alt' : ''} ${
                      isSelected ? 'is-selected' : ''
                    } ${isAssigned ? 'is-locked' : ''}`}
                    onClick={() => handleToggleRow(r)}
                  >
                    <div className="ab24-row-left">
                      <input
                        type="checkbox"
                        className="ab24-checkbox"
                        checked={isSelected}
                        disabled={isAssigned}
                        onChange={() => handleToggleRow(r)}
                        onClick={(e) => e.stopPropagation()}
                        title={
                          isAssigned
                            ? `Already assigned to ${r.assigned_to_name || 'staff'}`
                            : 'Select this reading'
                        }
                      />
                      <span className={`ab24-vital-badge ab24-vital--${r.vital_type}`}>
                        {shortVital}
                      </span>
                      <span className="ab24-time">{timeLabel}</span>
                    </div>

                    <div className="ab24-row-values">
                      {r.vital_type === 'bp' && r.systolic_pressure != null && r.diastolic_pressure != null ? (
                        <span className="ab24-vital-val">
                          <span style={{ color: getStatusColor(getBpSysStatus(r.systolic_pressure, patient)) }}>
                            {r.systolic_pressure}
                          </span>
                          <span className="ab24-slash">/</span>
                          <span style={{ color: getStatusColor(getBpDiaStatus(r.diastolic_pressure, patient)) }}>
                            {r.diastolic_pressure}
                          </span>
                          <span className="ab24-unit">mmHg</span>
                          {r.pulse ? (
                            <span
                              className="ab24-pulse"
                              style={{ color: getStatusColor(getPulseStatus(r.pulse, patient)) }}
                            >
                              ({r.pulse} bpm)
                            </span>
                          ) : null}
                        </span>
                      ) : r.vital_type === 'bg' && r.blood_glucose_value != null ? (
                        <span className="ab24-vital-val">
                          <span style={{ color: getStatusColor(getBgStatus(r.blood_glucose_value, patient)) }}>
                            {Math.round(r.blood_glucose_value)}
                          </span>
                          <span className="ab24-unit">mg/dL</span>
                        </span>
                      ) : r.vital_type === 'weight' && r.weight != null ? (
                        <span className="ab24-vital-val">
                          <span style={{ color: getStatusColor(getWeightStatus(r.weight, patient)) }}>
                            {Number(r.weight).toFixed(1)}
                          </span>
                          <span className="ab24-unit">lbs</span>
                        </span>
                      ) : (
                        <span className="ab24-vital-val">{r.reading_value}</span>
                      )}
                    </div>

                    <div className="ab24-row-status">
                      {isAssigned ? (
                        <span
                          className="ab24-lock-badge"
                          title={`Already assigned to ${r.assigned_to_name || 'staff'}. This selection stays on.`}
                        >
                          Assigned to {Number(r.assigned_to_user_id) === Number(currentUserId) ? 'you' : (r.assigned_to_name || 'staff')}
                        </span>
                      ) : (
                        <span className="ab24-avail-badge">Available</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ab24-footer">
          <button
            type="button"
            className="ab24-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ab24-confirm-btn"
            onClick={handleConfirm}
            disabled={selectedAvailableCount === 0}
          >
            Confirm Selection ({selectedAvailableCount})
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AbnormalReadings24hModal;
